/* eslint-disable no-unused-vars */
import FileAttachment from "./shared/FileAttachment";
import {
  PRIORITY_OPTIONS,
  SOURCE_OPTIONS,
} from "../../data/formOptions";
import { useImpactedModules } from "../../hooks/useImpactedModules";
import styles from "../../styles/forms/SharedForm.module.css";
import SplitFormLayout from "../shared/SplitFormLayout";
import { safeLocalStorageGet } from "../../utils/safeJsonParse";
import { getSubmitterInfo, getDefaultCountry } from "../../utils/formHelpers";

import { useFormState } from "../../hooks/useFormState";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import {
  FormSection,
  FormField,
  PinnedSubmit,
  SuccessCard,
} from "./shared/FormComponents";
import { FormProvider } from "./shared/FormContext";
import T, { useTranslate } from "../T";

const STEPS = [
  { id: "reporter", label: "Reporter info", desc: "Summary and description" },
  { id: "details", label: "Incident details", desc: "Severity and start time" },
  { id: "context", label: "Context", desc: "Impact and reproduction" },
  { id: "attachments", label: "Attachments", desc: "Relevant files & logs" },
];

const INITIAL = {
  summary: "",
  description: "",
  severity: "Sev-2 (Medium)",
  priority: "Medium",
  impactedModule: "",
  incidentStartTime: "",
  sourceOfRequest: "Business Team",
  workaroundAvailable: "No",
  stepsToReproduce: "",
  loanId: "",
  carId: "",
  dealerId: "",
  attachments: [],
};

const SEVERITY_CHIPS = [
  {
    value: "Sev-0 (Critical)",
    label: "Critical",
    desc: "System down",
    class: "Critical",
  },
  { value: "Sev-1 (High)", label: "High", desc: "Major impact", class: "High" },
  {
    value: "Sev-2 (Medium)",
    label: "Medium",
    desc: "Partial impact",
    class: "Medium",
  },
  { value: "Sev-3 (Low)", label: "Low", desc: "Minor issue", class: "Low" },
];

export default function SystemIncidentForm({ onBack }) {
  const { modules: impactedModules, isLoading: modulesLoading } = useImpactedModules();
  const { tText } = useTranslate();
  const {
    formData,
    setFormData,
    handleChange,
    handleAddFiles,
    handleRemoveFile,
    setAllTouched,
    contextValue,
  } = useFormState(INITIAL);

  const { submitForm, isSubmitting, submitted, error } = useFormSubmit();

  const onSubmit = () => {
    const profile = safeLocalStorageGet("staff_profile");
    const { submittedBy, creatorName } = getSubmitterInfo(profile);
    const country = getDefaultCountry(profile);

    submitForm(formData, (attachments) => {
      const nocobasePayload = {
        user_name: creatorName,
        category: 1,
        Summary: formData.summary,
        description: formData.description,
        impactedsystem: formData.impactedModule
          ? [formData.impactedModule]
          : [],
        priority: formData.priority,
        environment: "Production",
        loanid: formData.loanId || "",
        carid: formData.carId || "",
        dealerid: formData.dealerId || "",
        Description: `Severity: ${formData.severity}\n\n${formData.description}\n\nIncident Start Time: ${formData.incidentStartTime}\nWorkaround Available: ${formData.workaroundAvailable}\nSteps to Reproduce: ${formData.stepsToReproduce}`,
        Country: country,
        sourceofrequest: formData.sourceOfRequest,
        confluencelink: "",
        accesstype: "",
        accesslevel: "",
        linemanagername: "",
        linemanageremail: "",
        mailinglist: "",
        status: "Open",
        tickettype: "System Bugs & Incidents",
        Resolution: "",
        rootcause: "",
        f_7ygfivdcnao: "",
        Creator: creatorName,
        Creatoremail: submittedBy,
        attachement: attachments.map((a) => a.id),
      };

      return { nocobasePayload };
    });
  };

  if (submitted) {
    return (
      <SplitFormLayout
        badge="Internal Support"
        title="System Incident"
        subtitle="Report a live system outage or critical service disruption."
        steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
        currentStep={STEPS.length}
        middleBreadcrumb={{
          label: tText("System Issues"),
          path: "/staff/request/system-issues",
        }}
        currentBreadcrumb="Log Incident"
      >
        <SuccessCard
          title="Incident Logged"
          description="Your system incident has been logged. The on-call team will respond based on the severity level."
          onBack={onBack}
        />
      </SplitFormLayout>
    );
  }

  const formValid =
    formData.summary &&
    formData.description &&
    formData.severity &&
    formData.incidentStartTime &&
    formData.impactedModule &&
    formData.sourceOfRequest &&
    formData.stepsToReproduce;

  return (
    <SplitFormLayout
      badge="Internal Support"
      title="System Incident"
      subtitle="Report a live system outage or critical service disruption."
      steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
      middleBreadcrumb={{
        label: tText("System Issues"),
        path: "/staff/request/system-issues",
      }}
      currentBreadcrumb="Log Incident"
    >
      <FormProvider value={contextValue}>
        <form
          onSubmit={(e) => e.preventDefault()}
          className={styles.ticketFormWrapper}
        >
          {error && <div className={styles.errorBanner}>{tText(error)}</div>}

          <FormSection id="reporter" title="1. Reporter Context">
            <FormField
              label="Summary"
              name="summary"
              required
              style={{ marginBottom: "20px" }}
            >
              <input
                type="text"
                name="summary"
                value={formData.summary}
                onChange={handleChange}
                className={styles.input}
                placeholder={tText("Brief summary of the incident")}
                required
              />
            </FormField>
            <FormField label="Description" name="description" required>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={styles.textarea}
                rows={5}
                placeholder={tText("Describe the incident in detail...")}
                required
              />
            </FormField>
          </FormSection>

          <FormSection id="details" title="2. Incident Details">
            <FormField
              label="Severity"
              name="severity"
              required
              style={{ marginBottom: "24px" }}
            >
              <div className={styles.severityChips}>
                {SEVERITY_CHIPS.map((chip) => (
                  <div
                    key={chip.value}
                    className={`${styles.severityChip} ${styles[chip.class]} ${
                      formData.severity === chip.value ? styles.active : ""
                    }`}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, severity: chip.value }))
                    }
                  >
                    <span className={styles.severityChipTitle}>
                      <T>{chip.label}</T>
                    </span>
                    <span className={styles.severityChipDesc}><T>{chip.desc}</T></span>
                  </div>
                ))}
              </div>
            </FormField>

            <div className={styles.row2} style={{ marginBottom: "20px" }}>
              <FormField label="Priority" name="priority" required>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      <T>{p}</T>
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label="Source of Request"
                name="sourceOfRequest"
                required
              >
                <select
                  name="sourceOfRequest"
                  value={formData.sourceOfRequest}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  {SOURCE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      <T>{s}</T>
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
            <div className={styles.row2}>
              <FormField
                label="Incident Start Time"
                name="incidentStartTime"
                required
              >
                <input
                  type="datetime-local"
                  name="incidentStartTime"
                  value={formData.incidentStartTime}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </FormField>
              <FormField
                label="Workaround Available"
                name="workaroundAvailable"
              >
                <select
                  name="workaroundAvailable"
                  value={formData.workaroundAvailable}
                  onChange={handleChange}
                  className={styles.select}
                >
                  <option value="No"><T>No</T></option>
                  <option value="Yes"><T>Yes</T></option>
                  <option value="Partial"><T>Partial</T></option>
                </select>
              </FormField>
            </div>
          </FormSection>

          <FormSection id="context" title="3. Context & Impact">
            <FormField
              label="Impacted Module(s)"
              name="impactedModule"
              required
              style={{ marginBottom: "20px" }}
            >
              <select
                name="impactedModule"
                value={formData.impactedModule}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">{modulesLoading ? tText("Loading...") : tText("Select...")}</option>
                {impactedModules.map((m) => (
                  <option key={m} value={m}>
                    <T>{m}</T>
                  </option>
                ))}
              </select>
            </FormField>
            <FormField
              label="Steps to Reproduce"
              name="stepsToReproduce"
              required
              style={{ marginBottom: "20px" }}
            >
              <textarea
                name="stepsToReproduce"
                value={formData.stepsToReproduce}
                onChange={handleChange}
                className={styles.textarea}
                rows={3}
                placeholder={tText("Steps taken before failure...")}
              />
            </FormField>

            {/* Optional reference IDs */}
            <div className={styles.row3} style={{ marginTop: "4px" }}>
              <FormField label="Loan ID" name="loanId">
                <input
                  type="text"
                  name="loanId"
                  value={formData.loanId}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder="e.g. LN-00123"
                />
              </FormField>
              <FormField label="Car ID" name="carId">
                <input
                  type="text"
                  name="carId"
                  value={formData.carId}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder="e.g. CAR-00456"
                />
              </FormField>
              <FormField label="Dealer ID" name="dealerId">
                <input
                  type="text"
                  name="dealerId"
                  value={formData.dealerId}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder="e.g. DLR-00789"
                />
              </FormField>
            </div>
          </FormSection>

          <FormSection id="attachments" title="4. Attachments">
            <FileAttachment
              attachments={formData.attachments}
              onChange={handleAddFiles}
              onRemove={handleRemoveFile}
            />
          </FormSection>

          <PinnedSubmit
            isSubmitting={isSubmitting}
            isValid={formValid}
            onSubmit={onSubmit}
            onInvalidSubmit={setAllTouched}
            label="Log Incident"
          />
        </form>
      </FormProvider>
    </SplitFormLayout>
  );
}
